"use client";

import type React from "react";
import { CountryDialCode } from "@/lib/db/countries-dial-codes";
import { cn } from "@/lib/utils/index";
import { ChevronDown, Search, X } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface CountryDialCodeDropdownProps {
  countries: CountryDialCode[];
  selectedCode: string | null;
  onSelect: (code: string) => void;
  className?: string;
  disabled?: boolean;
  error?: boolean;
}

interface DropdownPosition {
  top: number;
  left: number;
  maxHeight: number;
  listMaxHeight: number;
  openUpward: boolean;
}

/** Panel's max width, also applied inline below — it can shrink narrower on small viewports. */
const DROPDOWN_WIDTH_PX = 320;
/** Panel's maximum height: max-h-96 = 24rem = 384px. */
const DROPDOWN_MAX_HEIGHT_PX = 384;
/** Smallest practical panel height before the list becomes hard to use. */
const DROPDOWN_MIN_HEIGHT_PX = 160;
/** Estimated search header height, used to size the scrollable country list. */
const DROPDOWN_SEARCH_HEADER_PX = 64;
/** Gap between the trigger button and the dropdown panel. */
const DROPDOWN_TRIGGER_GAP_PX = 4;
/** Minimum gap kept between the panel and the viewport edge. */
const VIEWPORT_MARGIN_PX = 8;
/**
 * Grace period after opening during which scroll/resize dismissal is suppressed.
 * The search input autofocuses on open, which summons the mobile keyboard; the
 * browser then auto-scrolls the page to keep that input visible above it. That
 * scroll fires on `document`, not on anything inside the dropdown, so without
 * this grace period it read as an outside dismissal and closed the panel the
 * instant it opened, before the user ever saw it.
 */
const SCROLL_DISMISS_GRACE_MS = 350;

interface ViewportBounds {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const getViewportBounds = (): ViewportBounds => {
  const viewport = window.visualViewport;
  const top = viewport?.offsetTop ?? 0;
  const left = viewport?.offsetLeft ?? 0;
  const width = viewport?.width ?? window.innerWidth;
  const height = viewport?.height ?? window.innerHeight;

  return {
    top,
    left,
    width,
    height,
    right: left + width,
    bottom: top + height,
  };
};

/** Dropdown for selecting a country dial code with search and flag display */
export const CountryDialCodeDropdown = ({
  countries,
  selectedCode,
  onSelect,
  className = "",
  disabled = false,
  error = false,
}: CountryDialCodeDropdownProps): React.ReactElement => {
  const searchId = useId();

  // States
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  // Refs
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Effects
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Derived values
  const filteredCountries = useMemo(() => {
    if (!searchTerm.trim()) {
      return countries.filter((country) => country && country.name && country.code && country.dial_code);
    }

    const searchLower = searchTerm.toLowerCase().replace(/\s+/g, "");
    return countries
      .filter((country) => country && country.name && country.code && country.dial_code)
      .filter((country) => {
        const nameNormalized = country.name?.toLowerCase().replace(/\s+/g, "") || "";
        const codeNormalized = country.code?.toLowerCase().replace(/\s+/g, "") || "";
        const dialCodeStr = String(country.dial_code || "")
          .toLowerCase()
          .replace(/\s+/g, "");

        return (
          nameNormalized.includes(searchLower) ||
          codeNormalized.includes(searchLower) ||
          dialCodeStr.includes(searchLower)
        );
      });
  }, [countries, searchTerm]);

  /** Calculates dropdown position based on the trigger button's bounding rect */
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewport = getViewportBounds();
    const maxUsableHeight = Math.max(0, viewport.height - VIEWPORT_MARGIN_PX * 2);
    const minPanelHeight = Math.min(DROPDOWN_MIN_HEIGHT_PX, maxUsableHeight);
    const naturalPanelHeight = Math.min(DROPDOWN_MAX_HEIGHT_PX, maxUsableHeight);
    const spaceBelow = viewport.bottom - rect.bottom - DROPDOWN_TRIGGER_GAP_PX - VIEWPORT_MARGIN_PX;
    const spaceAbove = rect.top - viewport.top - DROPDOWN_TRIGGER_GAP_PX - VIEWPORT_MARGIN_PX;
    const openUpward = spaceBelow < naturalPanelHeight && spaceAbove > spaceBelow;
    const sideSpace = Math.max(0, openUpward ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(naturalPanelHeight, Math.max(minPanelHeight, sideSpace));
    const listMaxHeight = Math.max(0, maxHeight - DROPDOWN_SEARCH_HEADER_PX);
    // Clamp so the fixed-width panel never overflows off the right edge on a
    // narrow viewport, while never pushing it past the left edge either.
    const panelWidth = Math.min(DROPDOWN_WIDTH_PX, Math.max(0, viewport.width - VIEWPORT_MARGIN_PX * 2));
    const minLeft = viewport.left + VIEWPORT_MARGIN_PX;
    const maxLeft = viewport.right - panelWidth - VIEWPORT_MARGIN_PX;
    const left = clamp(rect.left, minLeft, Math.max(minLeft, maxLeft));
    const preferredTop = openUpward
      ? rect.top - maxHeight - DROPDOWN_TRIGGER_GAP_PX
      : rect.bottom + DROPDOWN_TRIGGER_GAP_PX;
    const minTop = viewport.top + VIEWPORT_MARGIN_PX;
    const maxTop = viewport.bottom - maxHeight - VIEWPORT_MARGIN_PX;
    const top = clamp(preferredTop, minTop, Math.max(minTop, maxTop));

    setPosition({
      top,
      left,
      maxHeight,
      listMaxHeight,
      openUpward,
    });
  }, []);

  /** Opens the dropdown and calculates position */
  const handleToggle = useCallback(() => {
    if (disabled) return;
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen((prev) => !prev);
  }, [disabled, isOpen, updatePosition]);

  // Click outside handler — checks both button and dropdown refs
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      const isInsideButton = buttonRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);
      if (!isInsideButton && !isInsideDropdown) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [isOpen]);

  // Close dropdown on scroll or resize. The search input below autofocuses on
  // open, which summons the mobile keyboard — that resizes the viewport
  // *height* only, and treating it as "close" made the picker close itself
  // right after opening. Height-only viewport changes are positioning changes,
  // not dismissals.
  useEffect(() => {
    if (!isOpen) return;

    const handleClose = () => {
      setIsOpen(false);
      setSearchTerm("");
    };

    const widthAtOpen = getViewportBounds().width;
    let animationFrame = 0;
    const schedulePositionUpdate = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(updatePosition);
    };

    // Registered with `capture: true` so it sees scrolls anywhere in the
    // document — including the dropdown's own scrollable country list.
    // Scrolling that list to find a country is a normal mobile gesture, not a
    // dismissal; only close for outside scrolls after the keyboard grace period.
    const handleScroll = (event: Event) => {
      const target = event.target as Node;
      if (dropdownRef.current?.contains(target)) return;
      handleClose();
    };

    const handleResize = () => {
      if (Math.abs(getViewportBounds().width - widthAtOpen) > 1) {
        handleClose();
        return;
      }
      schedulePositionUpdate();
    };

    const visualViewport = window.visualViewport;
    window.addEventListener("resize", handleResize);
    visualViewport?.addEventListener("resize", handleResize);
    visualViewport?.addEventListener("scroll", schedulePositionUpdate);
    schedulePositionUpdate();

    // Delay attaching the scroll-dismiss listener so the autofocus-triggered "scroll the
    // input into view above the keyboard" adjustment (see
    // SCROLL_DISMISS_GRACE_MS above) has already happened by the time
    // they're live, rather than being misread as an outside dismissal.
    const timeoutId = window.setTimeout(() => {
      schedulePositionUpdate();
      window.addEventListener("scroll", handleScroll, true);
    }, SCROLL_DISMISS_GRACE_MS);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      visualViewport?.removeEventListener("resize", handleResize);
      visualViewport?.removeEventListener("scroll", schedulePositionUpdate);
    };
  }, [isOpen, updatePosition]);

  // Handlers
  const handleSelect = (code: string) => {
    onSelect(code);
    setIsOpen(false);
    setSearchTerm("");
  };

  if (countries.length === 0 || !countries[0]) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center justify-center gap-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 w-[85px] text-sm">
          <span className="text-xs text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  const selectedCountry = countries.find((c) => c && c.code === selectedCode) || countries[0];

  if (!selectedCountry || !selectedCountry.dial_code) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center justify-center gap-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 w-[85px] text-sm">
          <span className="text-xs text-gray-400">Error</span>
        </div>
      </div>
    );
  }

  // Ensure dial_code is a string
  const dialCodeStr = String(selectedCountry.dial_code || "");
  const displayDialCode = dialCodeStr.startsWith("+") ? dialCodeStr : `+${dialCodeStr}`;

  // Render
  const dropdownMenu =
    isOpen && position && isMounted
      ? createPortal(
          <div
            ref={dropdownRef}
            className="bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden"
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: `min(${DROPDOWN_WIDTH_PX}px, calc(100vw - ${VIEWPORT_MARGIN_PX * 2}px))`,
              maxHeight: position.maxHeight,
              zIndex: 999999,
            }}
          >
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-gray-400"
                  aria-hidden="true"
                />
                <label htmlFor={searchId} className="sr-only">
                  Search country
                </label>
                <input
                  id={searchId}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search country..."
                  className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-8 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 touch-manipulation transform -translate-y-1/2 text-gray-400 [@media(hover:hover)]:hover:text-gray-600"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto" role="listbox" style={{ maxHeight: position.listMaxHeight }}>
              {filteredCountries.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">No countries found</div>
              ) : (
                filteredCountries.map((country) => {
                  const dialCodeStr = String(country.dial_code || "");
                  const countryDialCode = dialCodeStr.startsWith("+") ? dialCodeStr : `+${dialCodeStr}`;
                  return (
                    <button
                      key={country.id}
                      type="button"
                      role="option"
                      aria-selected={country.code === selectedCode}
                      onClick={() => handleSelect(country.code)}
                      className={`flex w-full touch-manipulation items-center gap-3 px-4 py-2 text-left [@media(hover:hover)]:hover:bg-gray-100 ${
                        country.code === selectedCode ? "bg-blue-50" : ""
                      }`}
                    >
                      <span className="text-lg">{country.flag || "🌍"}</span>
                      <span className="flex-1 text-sm font-medium text-gray-700">{country.name || ""}</span>
                      <span className="text-sm text-gray-500">{countryDialCode}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-invalid={error || undefined}
        className={cn(
          "flex w-[85px] touch-manipulation items-center justify-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200",
          error && "border-destructive focus:border-destructive focus:ring-destructive/20",
          disabled ? "cursor-not-allowed bg-gray-100 opacity-60" : "bg-white [@media(hover:hover)]:hover:bg-gray-50"
        )}
      >
        <span className="text-sm">{selectedCountry.flag || "🌍"}</span>
        <span className="text-xs font-medium text-gray-700">{displayDialCode}</span>
        <ChevronDown className={`size-3 text-gray-500 transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {dropdownMenu}
    </div>
  );
};
